import { zodResolver } from "@hookform/resolvers/zod";
import { useController, useForm, useWatch } from "react-hook-form";
import { YStack } from "tamagui";
import { z } from "zod";

import { Button, Footer, Input, MaskedInput, Text } from "@/components";
import UnitToggle from "@/conversation/UnitToggle";
import { IMPERIAL_HEIGHT_MASK, UNICODE } from "@/utils/formatting";

const hasDigit = (s: string) => /\d/.test(s);
const hasAtLeastTwoDigits = (s: string) => s.replace(/\D/g, "").length >= 2;

const schema = z
  .object({
    unitSystem: z.enum(["imperial", "metric"]),
    heightImperial: z.string(),
    heightCm: z.string(),
    weightLbs: z.string(),
    weightKg: z.string(),
  })
  .refine((d) => d.unitSystem !== "imperial" || hasDigit(d.heightImperial), {
    message: "Please enter your height",
    path: ["heightImperial"],
  })
  .refine(
    (d) => d.unitSystem !== "imperial" || hasAtLeastTwoDigits(d.heightImperial),
    {
      message: "Please enter your height in feet and inches",
      path: ["heightImperial"],
    },
  )
  .refine((d) => d.unitSystem !== "imperial" || hasDigit(d.weightLbs), {
    message: "Please enter your weight",
    path: ["weightLbs"],
  })
  .refine(
    (d) => d.unitSystem !== "imperial" || hasAtLeastTwoDigits(d.weightLbs),
    {
      message: "Please enter at least two digits for your weight",
      path: ["weightLbs"],
    },
  )
  .refine((d) => d.unitSystem !== "metric" || hasDigit(d.heightCm), {
    message: "Please enter your height",
    path: ["heightCm"],
  })
  .refine((d) => d.unitSystem !== "metric" || hasDigit(d.weightKg), {
    message: "Please enter your weight",
    path: ["weightKg"],
  });

type FormData = z.infer<typeof schema>;

const sanitizeMetricWeight = (text: string) =>
  text.replace(/[^\d.]/g, "").replace(/^(\d*\.?\d*).*$/, "$1");

export interface HeightWeightSubmission {
  displayText: string;
  heightIn: number;
  weightLb: number;
}

interface HeightWeightInputProps {
  onSubmit: (submission: HeightWeightSubmission) => void;
}

const HeightWeightInput = ({ onSubmit }: HeightWeightInputProps) => {
  const { control, handleSubmit, setValue, clearErrors } = useForm<FormData>({
    resolver: zodResolver(schema),
    shouldFocusError: true,
    defaultValues: {
      unitSystem: "imperial",
      heightImperial: "",
      heightCm: "",
      weightLbs: "",
      weightKg: "",
    },
  });

  const unitSystem = useWatch({ control, name: "unitSystem" });
  const isImperial = unitSystem === "imperial";

  const {
    field: heightImperialField,
    fieldState: { error: heightImperialError },
  } = useController({ control, name: "heightImperial" });
  const {
    field: heightCmField,
    fieldState: { error: heightCmError },
  } = useController({ control, name: "heightCm" });
  const {
    field: weightLbsField,
    fieldState: { error: weightLbsError },
  } = useController({ control, name: "weightLbs" });
  const {
    field: weightKgField,
    fieldState: { error: weightKgError },
  } = useController({ control, name: "weightKg" });

  const handleToggle = (unit: "imperial" | "metric") => {
    setValue("unitSystem", unit);
    if (unit === "imperial") {
      setValue("heightCm", "");
      setValue("weightKg", "");
    } else {
      setValue("heightImperial", "");
      setValue("weightLbs", "");
    }
    clearErrors();
  };

  const onFormSubmit = handleSubmit((data) => {
    let heightText: string;
    let heightIn: number;
    if (data.unitSystem === "imperial") {
      const digits = data.heightImperial.replace(/\D/g, "");
      const feet = parseInt(digits.charAt(0) || "0");
      const inches = parseInt(digits.slice(1) || "0");
      heightText = `${feet}ft ${inches}in`;
      heightIn = feet * 12 + inches;
    } else {
      heightText = `${data.heightCm}cm`;
      heightIn = parseFloat(data.heightCm) / 2.54;
    }
    const weightLb =
      data.unitSystem === "imperial"
        ? parseInt(data.weightLbs)
        : parseFloat(data.weightKg) * 2.20462;
    const weightText =
      data.unitSystem === "imperial"
        ? `${data.weightLbs}lbs`
        : `${data.weightKg}kg`;
    onSubmit({
      displayText: `Height: ${heightText}, Weight: ${weightText}`,
      heightIn,
      weightLb,
    });
  });

  return (
    <YStack gap="$200" flex={1}>
      <YStack gap="$200" flex={1}>
        <UnitToggle unitSystem={unitSystem} onToggle={handleToggle} />
        <YStack gap="$100">
          <Text size="$sm" color="$textSecondary">
            Height
          </Text>
          {isImperial ? (
            <MaskedInput.Controlled
              ref={heightImperialField.ref}
              value={heightImperialField.value}
              mask={IMPERIAL_HEIGHT_MASK}
              onChangeText={heightImperialField.onChange}
              aria-label="Height"
              inputMode="numeric"
              placeholder={`-${UNICODE.PRIME} --${UNICODE.DOUBLE_PRIME}`}
              errorText={heightImperialError?.message}
            />
          ) : (
            <Input.Controlled
              ref={heightCmField.ref}
              value={heightCmField.value}
              onChangeText={(text) =>
                heightCmField.onChange(text.replace(/\D/g, ""))
              }
              aria-label="Height in centimeters"
              inputMode="numeric"
              placeholder="--"
              postfix={
                <Text size="$sm" color="$textSecondary">
                  cm
                </Text>
              }
              errorText={heightCmError?.message}
            />
          )}
        </YStack>
        <YStack gap="$100">
          <Text size="$sm" color="$textSecondary">
            Weight
          </Text>
          <Input.Controlled
            ref={isImperial ? weightLbsField.ref : weightKgField.ref}
            value={isImperial ? weightLbsField.value : weightKgField.value}
            onChangeText={(text) => {
              if (isImperial) {
                weightLbsField.onChange(text.replace(/\D/g, ""));
              } else {
                weightKgField.onChange(sanitizeMetricWeight(text));
              }
            }}
            aria-label={`Weight in ${isImperial ? "lbs" : "kg"}`}
            inputMode={isImperial ? "numeric" : "decimal"}
            placeholder="--"
            postfix={
              <Text size="$sm" color="$textSecondary">
                {isImperial ? "lbs" : "kg"}
              </Text>
            }
            errorText={(isImperial ? weightLbsError : weightKgError)?.message}
          />
        </YStack>
      </YStack>
      <Footer mode="dialog">
        <Button mode="primary" constrained onPress={onFormSubmit}>
          Continue
        </Button>
      </Footer>
    </YStack>
  );
};

export default HeightWeightInput;
